// import { call, fork, put, takeEvery } from 'redux-saga/effects';
import { all, call, fork, put, takeEvery, takeLeading } from 'redux-saga/effects';
import moment from 'moment';
import SweetAlert from '../../../../components/sweetalert/SweetAlert';

import * as types from './RawExamActionType';
import * as actions from './RawExamAction';
import * as services from './RawExamService';
import { downloadBase64File } from '../../../../utils/fileDownloads';
import { buildEpplusXlsx } from '../../../../utils/epplusXlsxWriter';
import { EXPORT_MODULE } from '../../../../constants/fileExport';
import {
    RAW_EXAM_EXPORT_HEADERS,
    RAW_EXAM_EXPORT_SHEET_NAME,
    toRawExamExportRow,
} from './rawExamExportColumns';

//------------------ Raw Exam Data - start------------------//
function* fetchRawExamDataSaga(action) {
    try {
        const response = yield call(services.getAllRawExamData, action.payload);
        if (!response) {
            throw new Error('No response received from API');
        }
        yield put(actions.fetchRawExamDataSuccess(response?.values));
    } catch (error) {
        let errorMessage = 'Failed to fetch raw exam data.';
        console.error('Raw Exam Data API Error:', {

            status: error.response?.status,
            message: errorMessage,
            data: error.response?.data,
            timestamp: new Date().toISOString()
        });
        yield put(actions.fetchRawExamDataFailure(errorMessage));
    }
}

function* refreshRawExamDataSaga(action) {
    try {
        const response = yield call(services.refreshRawExamData, action.payload);
        yield put(actions.refreshRawExamDataSuccess(response?.values));

        if (response && response.success) {
            SweetAlert.success({
                title: 'Success!',
                text: response?.resultMessage || 'Refreshed successfully.'
            });
        } else {
            SweetAlert.error({
                title: 'Error!',
                text: response?.errorMessage || 'Failed to refresh data.'
            });
        }
    } catch (error) {
        let errorMessage = 'Failed to refresh raw exam data.';
        yield put(actions.refreshRawExamDataFailure(errorMessage));
        SweetAlert.error({
            title: 'Error!',
            text: errorMessage
        });
    }
}
//------------------ Raw Exam Data - end------------------//

// Bypasses the shared exportToCSVRequest/commonSaga (Base64-in-JSON) flow: this export can return
// very large workbooks, so it downloads a binary blob directly instead of Base64-encoding + JSON-
// wrapping the file (see RawExamService.js).
// function* exportRawExamDataToCSVSaga(action) {
//     try {
//         const { fromDate, toDate, fileName } = action.payload || {};
//         const response = yield call(services.exportRawExamDataToCSV, fromDate, toDate);
//
//         const contentType = response.headers?.['content-type'] || '';
//         if (contentType.includes('application/json')) {
//             const errorText = yield response.data.text();
//             const errorBody = JSON.parse(errorText);
//             throw new Error(errorBody?.Message || errorBody?.message);
//         }
//
//         yield call(downloadBase64File, response.data, EXPORT_MODULE.RAW_EXAM_DATA, fileName);
//         yield put(actions.exportRawExamDataToCSVSuccess());
//         SweetAlert.success({
//             title: 'Success!',
//             text: 'Successfully exported data.'
//         });
//     } catch (error) {
//         console.error('Export Raw Exam Data API Error:', error);
//         yield put(actions.exportRawExamDataToCSVFailure(error?.message || 'Failed to export data.'));
//         SweetAlert.error({
//             title: 'Error!',
//             text: error?.message || 'Failed to export data.'
//         });
//     }
// }

// Large date ranges (100k+ rows) took longer than Azure App Service's 230-second request limit
// when the API built the whole workbook in one request. Instead, the data is fetched one record
// date per request (a few days at a time) and the workbook is built in the browser in exactly
// the format the API produced (see utils/epplusXlsxWriter.js and rawExamExportColumns.js).
const EXPORT_DAY_CONCURRENCY = 3;
const EXPORT_DAY_ATTEMPTS = 2;

const getDaysInRange = (fromDate, toDate) => {
    const start = moment(fromDate, 'MM/DD/YYYY', true);
    const end = moment(toDate, 'MM/DD/YYYY', true);
    if (!start.isValid() || !end.isValid()) {
        throw new Error('Failed to export data.');
    }
    const days = [];
    for (const day = start.clone(); day.isSameOrBefore(end, 'day'); day.add(1, 'day')) {
        days.push(day.format('MM/DD/YYYY'));
    }
    return days;
};

function* fetchRawExamDayRows(date) {
    for (let attempt = 1; ; attempt++) {
        try {
            const response = yield call(services.getRawExamDataByDate, date);
            const records = response?.values?.lstRawExamData;
            if (!Array.isArray(records)) {
                throw new Error('No response received from API');
            }
            return records.map(toRawExamExportRow);
        } catch (error) {
            if (attempt >= EXPORT_DAY_ATTEMPTS) {
                console.error(`Export Raw Exam Data API Error (${date}):`, error);
                throw new Error(`Failed to export data for ${date}.`);
            }
        }
    }
}

function* exportRawExamDataToCSVSaga(action) {
    try {
        const { fromDate, toDate, fileName } = action.payload || {};
        const days = getDaysInRange(fromDate, toDate);

        const rows = [];
        for (let i = 0; i < days.length; i += EXPORT_DAY_CONCURRENCY) {
            const batch = days.slice(i, i + EXPORT_DAY_CONCURRENCY);
            const batchRows = yield all(batch.map((day) => call(fetchRawExamDayRows, day)));
            batchRows.forEach((dayRows) => dayRows.forEach((row) => rows.push(row)));
        }

        const workbook = yield call(buildEpplusXlsx, {
            sheetName: RAW_EXAM_EXPORT_SHEET_NAME,
            headers: RAW_EXAM_EXPORT_HEADERS,
            rows,
        });

        yield call(downloadBase64File, workbook, EXPORT_MODULE.RAW_EXAM_DATA, fileName);
        yield put(actions.exportRawExamDataToCSVSuccess());
        SweetAlert.success({
            title: 'Success!',
            text: 'Successfully exported data.'
        });
    } catch (error) {
        console.error('Export Raw Exam Data API Error:', error);
        yield put(actions.exportRawExamDataToCSVFailure(error?.message || 'Failed to export data.'));
        SweetAlert.error({
            title: 'Error!',
            text: error?.message || 'Failed to export data.'
        });
    }
}

export default function* watchRawExamSaga() {
    yield takeEvery(types.FETCH_RAW_EXAM_DATA_REQUEST, fetchRawExamDataSaga);
    yield takeEvery(types.REFRESH_RAW_EXAM_DATA_REQUEST, refreshRawExamDataSaga);
    // yield takeEvery(types.EXPORT_RAW_EXAM_DATA_REQUEST, exportRawExamDataToCSVSaga);
    // takeLeading: ignore further Export clicks while an export is still running.
    yield takeLeading(types.EXPORT_RAW_EXAM_DATA_REQUEST, exportRawExamDataToCSVSaga);
}